import React, { useState, useEffect, useCallback } from 'react';
import { Search, Star, Eye, CheckCircle, XCircle, Shield, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../services/api';

const DRIVER_STATUS = {
  approved: { label: 'Aktiv', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Gesperrt', cls: 'bg-red-100 text-red-700' },
  rejected: { label: 'Abgelehnt', cls: 'bg-slate-100 text-slate-600' },
};

const PAGE_SIZE = 50;

export function DriversTabContent({ allDrivers, setAllDrivers, goToDriver, handleAction }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [driverFilter, setDriverFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadPage = useCallback(async (nextPage, statusFilter) => {
    setLoading(true);
    try {
      const params = { skip: nextPage * PAGE_SIZE, limit: PAGE_SIZE };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const r = await api.get('/admin/all-drivers', { params });
      const drivers = r.data?.drivers || [];
      setAllDrivers(drivers);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
      setPage(nextPage);
      setLoadedOnce(true);
    } catch (e) {
      console.error('[DriversTab] Failed to load:', e.message);
    } finally {
      setLoading(false);
    }
  }, [setAllDrivers]);

  // First load
  useEffect(() => {
    if (!loadedOnce) loadPage(0, driverFilter);
  }, []);

  // Reload page 0 whenever the status filter changes
  useEffect(() => {
    if (loadedOnce) loadPage(0, driverFilter);
  }, [driverFilter]);

  const reloadDrivers = () => loadPage(page, driverFilter);
  const nextPage = () => hasMore && loadPage(page + 1, driverFilter);
  const prevPage = () => page > 0 && loadPage(page - 1, driverFilter);

  // Client-side text search only filters the currently visible page.
  const filteredDrivers = allDrivers.filter(d => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return d.user?.name?.toLowerCase().includes(q)
        || d.user?.email?.toLowerCase().includes(q)
        || d.region?.toLowerCase().includes(q);
    }
    return true;
  });

  const fromIdx = page * PAGE_SIZE + (filteredDrivers.length ? 1 : 0);
  const toIdx = page * PAGE_SIZE + filteredDrivers.length;

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid="drivers-tab-content">
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-slate-50 rounded-lg px-3 py-2">
          <Search size={14} className="text-slate-400" />
          <input type="text" placeholder="Fahrer suchen (aktuelle Seite)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="bg-transparent outline-none text-sm flex-1" data-testid="driver-search" />
        </div>
        <select value={driverFilter} onChange={e => setDriverFilter(e.target.value)}
          className="bg-slate-50 rounded-lg px-3 py-2 text-sm outline-none" data-testid="driver-filter">
          <option value="all">Alle Status</option>
          <option value="approved">Aktiv</option>
          <option value="pending">Ausstehend</option>
          <option value="blocked">Gesperrt</option>
          <option value="rejected">Abgelehnt</option>
        </select>
        <button onClick={reloadDrivers} disabled={loading} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="reload-drivers-btn">
          <RefreshCw size={14} className={`text-slate-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <span className="text-xs text-slate-400" data-testid="drivers-count">
          {total > 0 ? `${fromIdx}–${toIdx} von ${total}` : `${filteredDrivers.length} Fahrer`}
        </span>
      </div>

      {loading && allDrivers.length === 0 ? (
        <div className="p-12 text-center">
          <Loader2 size={24} className="animate-spin text-orange-500 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Fahrer werden geladen...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Fahrer</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Region / Fahrzeug</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Zahlungskonten</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Bewertung</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Fahrten</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredDrivers.map(d => {
                const dv = d.vehicles?.[0] || d.vehicle || {};
                return (
                  <tr key={d.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => goToDriver(d.id)} data-testid={`driver-row-${d.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-800 text-sm">{d.user?.name}</p>
                      <p className="text-xs text-slate-400">{d.user?.email}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-sm text-slate-700">{d.region}</p>
                      <p className="text-xs text-slate-400">{dv.make} {dv.model} - {dv.plate_number}{(d.vehicles?.length || 0) > 1 ? ` (+${d.vehicles.length - 1})` : ''}</p>
                    </td>
                    <td className="px-5 py-3">
                      {d.paypal_email || d.instapay_number || d.vodafone_cash_number ? (
                        <div className="space-y-0.5">
                          {d.paypal_email && <p className="text-xs text-blue-600">PayPal: {d.paypal_email}</p>}
                          {d.instapay_number && <p className="text-xs text-green-600">InstaPay: {d.instapay_number}</p>}
                          {d.vodafone_cash_number && <p className="text-xs text-red-600">VodaCash: {d.vodafone_cash_number}</p>}
                        </div>
                      ) : <span className="text-xs text-slate-300">-</span>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1">
                        <Star size={13} className="text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium text-slate-700">{d.rating || '5.0'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-700">{d.total_trips || 0}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${DRIVER_STATUS[d.status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                        {DRIVER_STATUS[d.status]?.label || d.status}
                      </span>
                    </td>
                    <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <button onClick={() => goToDriver(d.id)} className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100" title="Details"><Eye size={14} /></button>
                        {d.status === 'pending' && (
                          <>
                            <button onClick={() => handleAction('approve', d.id)} className="p-1.5 bg-emerald-50 text-emerald-600 rounded hover:bg-emerald-100" title="Genehmigen"><CheckCircle size={14} /></button>
                            <button onClick={() => handleAction('reject', d.id)} className="p-1.5 bg-red-50 text-red-500 rounded hover:bg-red-100" title="Ablehnen"><XCircle size={14} /></button>
                          </>
                        )}
                        {d.status === 'approved' && (
                          <button onClick={() => handleAction('block', d.id)} className="p-1.5 bg-slate-100 text-slate-500 rounded hover:bg-red-50 hover:text-red-500" title="Sperren"><Shield size={14} /></button>
                        )}
                        {d.status === 'blocked' && (
                          <button onClick={() => handleAction('unblock', d.id)} className="p-1.5 bg-emerald-50 text-emerald-600 rounded hover:bg-emerald-100" title="Entsperren"><CheckCircle size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredDrivers.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400 text-sm">
                  {allDrivers.length === 0 ? 'Keine Fahrer gefunden. Klicken Sie den Refresh-Button.' : 'Keine Fahrer für diesen Filter.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls */}
      {total > PAGE_SIZE && (
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50" data-testid="drivers-pagination">
          <span className="text-xs text-slate-500">
            Seite {page + 1} von {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={prevPage}
              disabled={page === 0 || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="drivers-prev-page"
            >
              <ChevronLeft size={14} /> Zurück
            </button>
            <button
              onClick={nextPage}
              disabled={!hasMore || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-medium hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="drivers-next-page"
            >
              Weiter <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
